/**
 * Toast notification store — global toast/snackbar state via Zustand.
 */

import { create } from "zustand";
import type { ReactNode } from "react";

export type ToastSeverity = "success" | "error" | "warning" | "info";

export interface Toast {
  id: string;
  message: string;
  severity: ToastSeverity;
  duration?: number; // ms, default 4000
  action?: ReactNode;
}

interface ToastState {
  toasts: Toast[];
  addToast: (message: string, severity?: ToastSeverity, duration?: number, action?: ReactNode) => void;
  removeToast: (id: string) => void;
}

let counter = 0;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  addToast: (message, severity = "info", duration = 4000, action) => {
    const id = `toast-${++counter}`;
    set((s) => ({ toasts: [...s.toasts, { id, message, severity, duration, action }] }));
  },
  removeToast: (id) => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
}));

/** Convenience helpers for use outside React */
export const toast = {
  success: (msg: string, action?: ReactNode) => useToastStore.getState().addToast(msg, "success", 4000, action),
  error: (msg: string) => useToastStore.getState().addToast(msg, "error", 6000),
  warning: (msg: string) => useToastStore.getState().addToast(msg, "warning"),
  info: (msg: string) => useToastStore.getState().addToast(msg, "info"),
};
